import { BackBar, BackButton } from '../../layout/back-button';
import React, { useState } from 'react';
import { LogoMark } from '../../logo/logo-mark';
import { RightArrow } from '../../icons/right-arrow';
import { publicApi } from '../../../api/public-client';
import { AutoHeight, Shake, StateSwitch, StatusLabel } from '../../../motion';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
  Field,
  Label,
  Input,
  FullWidthButton,
  FieldError,
  HelperText,
  InfoBanner,
} from '../onboarding.styled';

// "I forgot my password", step one. Open to every role — the server resolves the
// address to whatever account holds it and never asks what kind of user it is.
//
// The confirmation NEVER says whether the address exists. That is not vagueness
// for its own sake: this page is unauthenticated and takes an email, so a
// different answer for a known address would turn it into a free membership
// oracle against every customer's user list. The server answers identically for
// both (see src/password-reset.js), and this copy has to match or it would leak
// what the API refuses to.
export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Enter the email you sign in with');
      setAttempt(n => n + 1);

      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await publicApi.post('/password/forgot', { email: email.trim() });
      setSent(true);
    } catch {
      // The endpoint answers 200 even for an unknown address and even on a
      // server fault, so reaching here means the request never arrived.
      setError('We could not reach the server. Please try again.');
      setAttempt(n => n + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell>
      <Card>
        {/* TOP LEFT, the product's one back control. These cards carried a
            <TopLink> anchor styled as body copy at the FOOT of the card — the
            name said top, the position said bottom, and on the reset form it
            sat under a submit button so the way out was below the fold. */}
        <BackBar>
          <BackButton to="/" label="Back to sign in" />
        </BackBar>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>

        {/* The form and the confirmation are two fills of the SAME card: the
            one crossfades into the other and the card tweens to the new
            height, rather than a silent JSX swap at the same positions. */}
        <AutoHeight>
          <StateSwitch state={sent ? 'sent' : 'form'}>
            {sent ? (
              <>
                <Heading>Check your email</Heading>
                <Subheading>
                  If that email belongs to a Lobbero account, a reset link is on
                  its way. It works once, and only for a short while.
                </Subheading>
                <InfoBanner>Sent to {email}</InfoBanner>
                <HelperText>
                  Nothing arrived? Check your spam folder, then try again — a
                  new link replaces the old one.
                </HelperText>
              </>
            ) : (
              <>
                <Heading>Reset your password</Heading>
                <Subheading>
                  Enter the email you sign in with and we&apos;ll send you a
                  link to choose a new password.
                </Subheading>

                <form onSubmit={submit}>
                  <Field>
                    <Label htmlFor="fp-email">Email</Label>
                    <Shake active={!!error} attempt={attempt}>
                      <Input
                        id="fp-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        hasError={!!error}
                        placeholder="you@example.com"
                      />
                    </Shake>
                  </Field>
                  <FieldError message={error || undefined} />
                  <FullWidthButton type="submit" disabled={submitting}>
                    <StatusLabel
                      state={submitting ? 'busy' : 'idle'}
                      labels={{ idle: 'Send reset link', busy: 'Sending…' }}
                    />{' '}
                    <RightArrow />
                  </FullWidthButton>
                </form>
              </>
            )}
          </StateSwitch>
        </AutoHeight>
      </Card>
    </Shell>
  );
};
